import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Card, CardContent, CircularProgress, Box } from '@mui/material';
import TourForm from './TourForm';
import ToursTable from './ToursTable';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { RequireAuth } from 'components/Auth/RequireAuth';
import { useInitializeTours, useTourForm, useTourTable } from '../../../hooks/useTours';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    '&&&&': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      margin: '0 auto',
      width: 'auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  headLineStyle: {
    fontWeight: '500',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  contentContainer: {
    width: '100%',
    color: theme.palette.text.primary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  formSection: {
    marginTop: theme.spacing(4),
  },
}));

export default function TourPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  useDocumentTitle(t('navigation.tours'));
  const sessionToken = localStorage.getItem('session_token');

  // Initialize tour data
  const {
    initializeData,
    isLoading: isInitializing,
    hasError: hasInitError,
  } = useInitializeTours();

  // Tour form management
  const {
    editingTour,
    isLoading: isFormLoading,
    error: formError,
    handleSubmit,
    handleEdit,
    handleCancel,
    clearError,
  } = useTourForm();

  // Tour table management
  const {
    tours,
    discords,
    rulesets,
    isLoading: isTableLoading,
    error: tableError,
    handleDelete,
  } = useTourTable();

  // Initialize data on component mount
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Handle success/error messages
  useEffect(() => {
    if (formError) {
      if (formError.includes('create')) {
        toast.error(t('tours.messages.createError'));
      } else if (formError.includes('update')) {
        toast.error(t('tours.messages.updateError'));
      }
      clearError('creating');
      clearError('updating');
    }
  }, [formError, t, clearError]);

  useEffect(() => {
    if (tableError && tableError.includes('delete')) {
      toast.error(t('tours.messages.deleteError'));
      clearError('deleting');
    }
  }, [tableError, t, clearError]);

  if (!sessionToken) {
    return <Navigate to="/login" replace />;
  }

  const isLoading = isInitializing || isTableLoading;
  const isError = hasInitError || tableError;

  return (
    <RequireAuth requiredPermission="is_staff">
      <GridItem md={12} className={classes.mainContainer}>
        <div className={classes.contentContainer}>
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Card>
              <CardContent>
                <div>{t('common.errorLoadingData')}</div>
              </CardContent>
            </Card>
          ) : (
            <GridContainer justify="center">
              {/* Tours Table Section */}
              <GridItem xs={12} className={classes.section}>
                <ToursTable
                  tours={tours || []}
                  discords={discords || []}
                  rulesets={rulesets || []}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </GridItem>

              {/* Tour Form Section */}
              <GridItem xs={12} id="tour-form-section" className={classes.formSection}>
                <TourForm
                  tour={editingTour || undefined}
                  discords={discords}
                  rulesets={rulesets}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  isLoading={isFormLoading}
                />
              </GridItem>
            </GridContainer>
          )}
        </div>
      </GridItem>
    </RequireAuth>
  );
}
